/*
  Keeps the per-unit / per-100g line in step with the selected variant.

  Every string was rendered server-side by snippets/shintop-unit-price.liquid, so this file
  only ever swaps text — it never formats money. That keeps currency and money-format
  handling entirely in Liquid, where Shopify owns it.

  Subscribes to Dawn's own variant-change event via the global `subscribe` from
  assets/pubsub.js (a classic script, so its top-level functions are global). Nothing in
  Dawn is patched or overridden. If pubsub is ever unavailable the element simply keeps the
  server-rendered value, which is correct for the variant the page loaded with.
*/
if (!customElements.get('shintop-unit-price')) {
  customElements.define(
    'shintop-unit-price',
    class ShintopUnitPrice extends HTMLElement {
      connectedCallback() {
        this.perUnitEl = this.querySelector('[data-shintop-per-unit]');
        this.per100El = this.querySelector('[data-shintop-per-100]');
        this.sepEl = this.querySelector('[data-shintop-sep]');
        this.data = this.readData();

        if (typeof subscribe !== 'function' || !this.data) return;

        const eventName =
          typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange
            ? PUB_SUB_EVENTS.variantChange
            : 'variant-change';

        this.unsubscribe = subscribe(eventName, ({ data }) => {
          if (!data || String(data.sectionId) !== String(this.dataset.section)) return;
          this.render(data.variant);
        });
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      readData() {
        const node = this.querySelector('[data-shintop-unit-price-data]');
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch {
          return null;
        }
      }

      render(variant) {
        // Variant unavailable (Dawn passes null): leave the last known values in place
        // rather than blanking the line, which would read as "this has no unit price".
        if (!variant) return;

        /*
          S03-13 (audit 2026-09-13, chunk 9). The other half of the sticky bar's defect. A
          variant with no row in the baked map used to hit a bare `if (!entry) return`, which
          left the previous variant's per-unit and per-100 figures on screen beside the new
          variant's price. A unit price for a different pack size is not a stale number, it is
          a wrong one, and a shopper comparing two sizes is exactly who reads this line.

          Clear it instead: no basis is a legible state, a wrong basis is not. Different from
          the `!variant` branch above on purpose — there the selection is merely unavailable
          and the line still describes what is shown; here the selection changed and the line
          no longer describes anything.

          Latent: 0 of 89 products are multi-variant.
        */
        const entry = this.data[variant.id];
        if (!entry) {
          if (this.perUnitEl) this.perUnitEl.textContent = '';
          if (this.per100El) this.per100El.textContent = '';
          if (this.sepEl) this.sepEl.hidden = true;
          return;
        }

        const perUnit = entry.perUnit || '';
        const per100 = entry.per100 || '';

        if (this.perUnitEl) this.perUnitEl.textContent = perUnit;
        if (this.per100El) this.per100El.textContent = per100;
        if (this.sepEl) this.sepEl.hidden = !(perUnit && per100);
      }
    },
  );
}
